/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";
import { Formik } from "formik";
import * as Yup from "yup";
import Payment from "payment";

import { Text, CardExpiryDate, Checkbox } from "../Forms";
import { SaveButton, ResetButton } from "../../components";
import * as select from "../../reducers";
import * as actionsSettings from "../../actions/settings";
import * as actionsModals from "../../actions/modals";

import visa from "../../assets/images/logo-visa.png";
import mastercard from "../../assets/images/logo-mastercard.png";
import { storeCreditCard, storeDirectDebit } from "../../api/Bpoint";
import moment from "moment";

const validExpiryDate = (value) =>
  !value?.length ||
  (value.length === 5 && moment(value, "MM/YY").isSameOrAfter(moment(), "M"));

const validationSchema = Yup.object().shape(
  {
    // direct_debit_name required if there are values for direct_debit_number and direct_debit_bsb or there is no CC...
    direct_debit_name: Yup.string()
      .when(
        ["direct_debit_number", "direct_debit_bsb", "credit_card_expiry_date"],
        (
          direct_debit_number,
          direct_debit_bsb,
          credit_card_expiry_date,
          schema
        ) => {
          return !!direct_debit_number ||
            !!direct_debit_bsb ||
            !validExpiryDate(credit_card_expiry_date)
            ? schema.required("Please provide an account name")
            : schema.notRequired();
        }
      )
      .label("Account Name"),
    // direct_debit_number required if the values for direct_debit_name and direct_debit_bsb exist and have changed or there is no CC...
    direct_debit_number: Yup.string()
      .when(
        [
          "direct_debit_name",
          "_direct_debit_name",
          "direct_debit_bsb",
          "_direct_debit_bsb",
          "credit_card_expiry_date",
        ],
        {
          is: (
            direct_debit_name,
            initial_direct_debit_name,
            direct_debit_bsb,
            initial_direct_debit_bsb,
            credit_card_expiry_date
          ) => {
            return (
              (direct_debit_name &&
                direct_debit_name !== initial_direct_debit_name) ||
              (direct_debit_bsb &&
                direct_debit_bsb?.replace(" ", "") !==
                  initial_direct_debit_bsb) ||
              !validExpiryDate(credit_card_expiry_date)
            );
          },
          then: Yup.string().required("Account number is required"),
          otherwise: Yup.string(),
        }
      )
      .label("Account Number"),
    // direct_debit_bsb required if there are values for direct_debit_name and direct_debit_number or there is no CC...
    direct_debit_bsb: Yup.string()
      .when(
        ["direct_debit_name", "direct_debit_number", "credit_card_expiry_date"],
        (
          direct_debit_name,
          direct_debit_number,
          credit_card_expiry_date,
          schema
        ) => {
          return !!direct_debit_number ||
            !!direct_debit_name ||
            !validExpiryDate(credit_card_expiry_date)
            ? schema.required("Please enter the account's BSB number")
            : schema.notRequired();
        }
      )
      .test({
        name: "isValidBSB",
        message: "BSB number is the wrong length (should be 6 characters)",
        test: (value) => {
          const { length } = (value || " ").replace(/ /g, "");
          return !value?.length || length === 6;
        },
      })
      .label("BSB Number"),
    // credit_card_name required if changing credit card number...
    credit_card_name: Yup.string()
      .when("credit_card_number", {
        is: (val) => val && val.trim().length > 0,
        then: Yup.string().required(),
        otherwise: Yup.string(),
      })
      .label("Name on Card"),
    // credit_card_number required if there is no Bank Account or credit_card_name or credit_card_expiry_date has changed....
    credit_card_number: Yup.string()
      .when(["credit_card_name", "_credit_card_name"], {
        is: (val, initialVal) => val !== initialVal && val?.trim().length > 0,
        then: Yup.string().required("Credit card number is required"),
        otherwise: Yup.string(),
      })
      .test({
        name: "isMasterCardOrVisa",
        message: "Please enter a valid VISA or Mastercard number",
        test: (value) =>
          !value?.length ||
          !value.trim().length ||
          ([15, 16].includes(value.replace(/ /g, "").length) &&
            ["visa", "mastercard"].includes(Payment.fns.cardType(value))),
      }),
    // credit_card_ccv required if there a credit_card_number...
    credit_card_ccv: Yup.string()
      .when("credit_card_number", {
        is: (val) => val && val.trim().length > 0,
        then: Yup.string().required(),
        otherwise: Yup.string(),
      })
      .test({
        name: "isValidCCV",
        message: "Please enter a valid CCV",
        test: (value) =>
          !value?.length ||
          (value.trim().length === 3 && value.match(/[0-9]/g)),
      })
      .label("CCV"),
    // credit_card_expiry_date required if there a credit_card_number...
    credit_card_expiry_date: Yup.string()
      .when("credit_card_number", {
        is: (val) => val && val.trim().length > 0,
        then: Yup.string().required(),
        otherwise: Yup.string(),
      })
      .test({
        name: "isValidDate",
        message: "Please enter a valid expiry date",
        test: (val) => validExpiryDate(val),
      })
      .label("CCV"),
    // Ts abd Cs are always required...
    terms_agree: Yup.boolean()
      .required("Please confirm that you agree to the terms and conditions.")
      .oneOf(
        [true],
        "Please confirm that you agree to the terms and conditions."
      ),
  },
  [
    ["direct_debit_name", "direct_debit_number"],
    ["direct_debit_name", "direct_debit_bsb"],
    ["direct_debit_number", "direct_debit_bsb"],
    ["credit_card_number", "credit_card_name"],
  ]
);

function SettingsBillingForm({
  initialValues,
  settingsBillingSaving,
  settingsBillingError,
  settingsBillingSuccess,
  settingsBillingDone,
  setModalTermsOpen,
  handleExpiredCard,
}) {
  const buttonType = useMemo(() => {
    switch (true) {
      case settingsBillingDone:
        return "";
      case settingsBillingSaving:
        return "saving";
      case settingsBillingError:
        return "error";
      case settingsBillingSuccess:
        return "success";
      default:
        return "";
    }
  }, [
    settingsBillingDone,
    settingsBillingError,
    settingsBillingSaving,
    settingsBillingSuccess,
  ]);

  const onSubmit = useCallback(
    async (values, { setValues, setErrors, resetForm, ...form }) => {
      if (values.direct_debit_number?.length) {
        await storeDirectDebit(
          {
            ...values,
            direct_debit_number: values.direct_debit_number.replace(/ /g, ""),
            direct_debit_bsb: values.direct_debit_bsb.replace(/ /g, ""),
          },
          (res) => {
            resetForm();
            setValues({
              ...values,
              direct_debit_number: "",
              masked_direct_debit_number:
                res.accounts?.direct_debit?.number_masked,
            });
          },
          (err) => {
            setErrors(err);
          }
        );
        await new Promise((resolve) => setTimeout(resolve, 1500));
      }
      if (values.credit_card_number?.length) {
        await storeCreditCard(
          values,
          (res) => {
            handleExpiredCard(res.accounts?.credit_card?.id);
            resetForm();
            setValues({
              ...values,
              credit_card_number: "",
              masked_credit_card_number:
                res.accounts?.credit_card?.number_masked,
            });
          },
          setErrors
        );
        await new Promise((resolve) => setTimeout(resolve, 1500));
      }
    },
    [handleExpiredCard]
  );

  return (
    <Formik
      validationSchema={validationSchema}
      initialValues={{
        ...initialValues,
        _credit_card_name: initialValues.credit_card_name,
        _credit_card_expiry_date: initialValues.credit_card_expiry_date,
        _direct_debit_name: initialValues.direct_debit_name,
        _direct_debit_bsb: initialValues.direct_debit_bsb,
      }}
      onSubmit={onSubmit}
    >
      {({ handleSubmit, errors, touched, resetForm, isSubmitting, values }) => {
        return (
          <form
            id="settingsBillingForm"
            className="form"
            onSubmit={handleSubmit}
          >
            <fieldset>
              <legend className="settings__heading">Direct Debit</legend>

              <fieldset>
                <p className="form__alert">
                  AFES maintains only one active Bank Account and one active
                  Credit Card per supporter. Updating your account or credit
                  card will replace previously held financial information
                </p>
              </fieldset>

              <fieldset className="fieldset__sub-section">
                <legend className="form__sub-section">Bank Account</legend>

                <div className="flex-row form__row">
                  <div className="col-12">
                    <Text label="Account Name" name="direct_debit_name" />
                  </div>
                </div>

                <div className="flex-row form__row">
                  <div className="col-6">
                    <Text
                      mask="999 999"
                      label="BSB Number"
                      name="direct_debit_bsb"
                    />
                  </div>
                  <div className="col-6">
                    <Text
                      mask="999 999999"
                      label="Account Number"
                      name="direct_debit_number"
                      placeholder={
                        values.masked_direct_debit_number || "123 456789"
                      }
                    />
                  </div>
                </div>
              </fieldset>

              <fieldset className="fieldset__sub-section">
                <legend className="form__sub-section flex-row stretch flex-row--aligncenter">
                  Credit Card
                  <div className="cc-logos flex-row flex-row--aligncenter">
                    <img src={visa} alt="visa" />
                    <img src={mastercard} alt="mastercard" />
                  </div>
                </legend>

                <div className="flex-row form__row">
                  <div className="col-12">
                    <Text label="Name on Card" name="credit_card_name" />
                  </div>
                </div>

                <div className="flex-row form__row max_row">
                  <div className="col-6">
                    <Text
                      mask="9999 9999 9999 9999"
                      label="Number"
                      name="credit_card_number"
                      placeholder={
                        values.masked_credit_card_number ||
                        "4574 4874 0535 1567"
                      }
                    />
                  </div>
                  <div className="col-4">
                    <CardExpiryDate
                      mask="99/99"
                      label="Expiration Details"
                      name="credit_card_expiry_date"
                    />
                  </div>
                  <div className="col-2">
                    <Text
                      label="CCV"
                      name="credit_card_ccv"
                      className="flex-row__auto form__ccv"
                      placeholder="***"
                      type="password"
                    />
                  </div>
                </div>
              </fieldset>
              <fieldset>
                <label
                  className={
                    "form__alert small form_checkbox_basic mb--24" +
                    (errors.terms_agree && touched.terms_agree ? " error" : "")
                  }
                >
                  <Checkbox inputWrapperClassName="flex-row" name="terms_agree">
                    I have read and agree to the&nbsp;
                    <a onClick={setModalTermsOpen}>terms and conditions</a>
                    &nbsp;for direct debit.
                  </Checkbox>
                </label>

                <div className="form_row form_note security_notice">
                  <i className="material-icons">lock</i>
                  <span>
                    Please note that AFES does not hold your bank account or
                    credit card details. These details are saved securely with
                    our banking partner Commonwealth Bank of Australia.
                  </span>
                </div>
              </fieldset>
            </fieldset>

            <div className="flex-row form__finalise-changes">
              <ResetButton action="reset" onClick={resetForm}>
                Reset
              </ResetButton>
              <SaveButton
                variant={isSubmitting ? "saving" : buttonType}
                onClick={handleSubmit}
                onTryAgain={handleSubmit}
              >
                Save Changes
              </SaveButton>
            </div>
          </form>
        );
      }}
    </Formik>
  );
}

const mapStateToProps = (state, ownProps) => {
  return {
    settingsBillingSaving: select.getSettingsBillingSaving(state),
    settingsBillingError: select.getSettingsBillingError(state),
    settingsBillingSuccess: select.getSettingsBillingSuccess(state),
    settingsBillingDone: select.getSettingsBillingDone(state),
  };
};

export default connect(mapStateToProps, {
  ...actionsSettings,
  ...actionsModals,
})(SettingsBillingForm);
